import noop from 'lib/function/noop'
import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from 'react'
import { useIntl } from 'react-intl'
import { useRebrandOverlayButtonKind } from 'hooks/useRebrandOverlayButtonProps'
import * as Analytics from 'analytics/analytics'
import LoadingAnimation from 'components/Common/Loading/LoadingAnimation'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import ChatMapWidget from 'tripPlanner/components/Chat/ChatMapWidget'
import ChatMapFilters from 'tripPlanner/components/Chat/ChatMapFilters'
import ChatMapOffersDrawer, { type ChatMapDrawerResults } from '../components/ChatMapOffersDrawer'
import type { MarkerSelectionData } from 'tripPlanner/components/Chat/chatMapConstants'
import type { MapCameraPosition, OfferMapSuggestionData, PlaceMapSuggestionData, TripStop } from 'tripPlanner/api/types'
import type { RightPanelView } from '../hooks/useRightPanelState'
import { useSearchResults } from '../hooks/useSearchResults'
import { useMapFilterState } from '../hooks/useMapFilterState'
import { useAICameraState } from '../hooks/useAICameraState'
import { useMapData } from '../hooks/useMapData'
import {
  createOfferSuggestionMarker,
  createPlaceSuggestionMarker,
  isValidCoordinate,
  MARKER_SOURCE_ITINERARY,
  MARKER_TYPE_CHAT_EXPERIENCE,
  MARKER_TYPE_CHAT_HOTEL,
  MARKER_TYPE_STOP,
  type MapMarkerComponent,
  type MapCoordinate,
} from 'tripPlanner/utils/mapMarkerTransforms'
import { useEventSubscription, useTripPlannerEventBus } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import { useChatActions } from '../contexts/ChatActionsContext'
import { useChatStreamStatus } from 'tripPlanner/assistant/contexts/ChatStreamStatusContext'
import { useChatUiAction } from '../contexts/ChatUiActionContext'
import { useChatStreamMessages } from '../contexts/ChatStreamMessagesContext'
import { useItineraryContext } from '../contexts/ItineraryContext'
import { useMapInteraction } from '../contexts/MapInteractionContext'
import { useCruiseTransportMapMarkers } from '../hooks/useCruiseTransportMapMarkers'
import { useChatMapOnMapMarkers } from '../hooks/useChatMapOnMapMarkers'
import { useTripMapActivation } from '../hooks/useTripMapActivation'
import { getOfferItineraryDayRange } from 'tripPlanner/utils/chatItinerary'
import { isExperienceSavedData } from 'tripPlanner/utils/savedItemDataGuards'
import type { ChatExploreCategory, ChatExploreCategoryKey } from 'tripPlanner/config/exploreCategories'
import { chatExploreCategories, englishExploreCategoryLabel } from 'tripPlanner/config/exploreCategories'
import { useExploreHandoffCamera } from '../hooks/useExploreHandoffCamera'
import { useExploreCamera } from '../hooks/useExploreCamera'
import { buildMapExploreCategorySelectedActivityEntry } from './buildMapExploreCategorySelectedActivityEntry'
import { buildMapSearchThisAreaActivityEntry } from './buildMapSearchThisAreaActivityEntry'
import type { ExploreItem } from 'tripPlanner/types/common'
import {
  FullHeightMapWrapper,
  MAP_KEEP_ALIVE_HIDDEN_CLASS,
  MapArea,
  MapOverlayStack,
  MapUpdatingIndicatorPill,
  SearchThisAreaButton,
  SHOW_ON_MAP_ZOOM,
  PLACE_SHOW_ON_MAP_ZOOM,
} from '../styles/layoutStyles'

interface Props {
  /** When false, hooks still run. After the first show, the Mapbox widget stays mounted and is CSS-hidden. */
  visible: boolean
  selectedDayItineraryId: number | undefined
  selectedStop: TripStop | undefined
  selectedMarkerData: MarkerSelectionData | undefined
  onMarkerSelect: (data: MarkerSelectionData | undefined) => void
  setRightPanelView: Dispatch<SetStateAction<RightPanelView>>
  rightPanelView: RightPanelView
}

function getMarkerIdentityKey(marker: MapMarkerComponent) {
  return marker.offerId ?? marker.markerId ?? `${marker.lat}:${marker.lng}:${marker.label ?? ''}`
}

const EMPTY_FIT_BOUNDS_REQUEST: { coordinates: Array<MapCoordinate>; key: number } = {
  coordinates: [],
  key: 0,
}

function MapContainer({
  visible,
  selectedDayItineraryId,
  selectedStop,
  selectedMarkerData,
  onMarkerSelect,
  setRightPanelView,
  rightPanelView,
}: Props) {
  const intl = useIntl()
  const overlayButtonKind = useRebrandOverlayButtonKind()
  const eventBus = useTripPlannerEventBus()
  const { activatePlace, activateOffer } = useTripMapActivation()
  const [offerSuggestionMarker, setOfferSuggestionMarker] = useState<MapMarkerComponent | undefined>(undefined)
  const [placeSuggestionMarker, setPlaceSuggestionMarker] = useState<MapMarkerComponent | undefined>(undefined)
  const [focusedPlaceId, setFocusedPlaceId] = useState<string | undefined>(undefined)
  const [focusedOfferId, setFocusedOfferId] = useState<string | undefined>(undefined)
  const [fitBoundsRequest, setFitBoundsRequest] = useState<{
    coordinates: Array<MapCoordinate>
    key: number
  }>(EMPTY_FIT_BOUNDS_REQUEST)
  const [lastMapCamera, setLastMapCamera] = useState<MapCameraPosition | undefined>(undefined)
  // Create Mapbox only after the first visible show (e.g. cart-first desktop mount).
  const [hasMountedMap, setHasMountedMap] = useState(visible)
  const shouldSnapshotNextMapCameraRef = useRef(false)

  // ── Context reads ──

  const { sendMessage } = useChatActions()
  const { chatId, chatKey, isAwaitingMessageEnd, loadingState } = useChatStreamStatus()
  const { pendingUiAction, clearPendingUiAction } = useChatUiAction()
  const { messages } = useChatStreamMessages()
  const { itineraryData, enrichingStopIds, enrichingItemIds } = useItineraryContext()
  const {
    onMarkerHover,
    onCardHover,
    highlightedMarkerId,
    mapStyleMode,
    onMapStyleModeChange,
    itineraryResult,
  } = useMapInteraction()

  // ── Hotel and experience search results ──

  const {
    searchQuery: hotelSearchQuery,
    hotelMapMarkers,
    isHotelFilterVisible,
    isHotelSearchActive,
    setHotelSearchResults,
    setHotelSearchError,
    setHotelSearchResultsFromCarousel,
    hideHotelFilter,
    resetHotelSearch,
    experienceSearchQuery,
    experienceMapMarkers,
    isExperienceFilterVisible,
    isExperienceSearchActive,
    setExperienceSearchResults,
    setExperienceSearchError,
    setExperienceSearchResultsFromCarousel,
    hideExperienceFilter,
    resetExperienceSearch,
  } = useSearchResults()

  // ── Map filter state ──

  const {
    mapBounds,
    handleMapViewStateChange,
    handleMapBoundsChange,
    handleHotelFilterDismiss,
    handleExperienceFilterDismiss,
    handleExploreCategorySelect,
    applyHotelFilterState,
    applyExperienceFilterState,
    handleExploreMapFromDrawer,
    notifyUserMovedMap,
    handleSearchThisAreaClick,
    shouldShowSearchThisArea,
    isSearchThisAreaDisabled,
    deemphasizeBaseMarkers,
    disableClustering,
    selectedExploreCategory,
    exploreMarkers,
    exploreOffers,
    explorePlaces,
    exploreHandoff,
    exploreSearchTarget,
    isExploreFilterVisible,
    isExploreFilterActive,
    isExploreLoading,
    isExploreFetching,
    isExploreError,
    isExplorePartial,
    refetchExplore,
    resetFilterState,
  } = useMapFilterState({
    setRightPanelView,
    isHotelFilterVisible: isHotelSearchActive,
    hideHotelFilter,
    setHotelSearchResultsFromCarousel,
    isExperienceFilterVisible: isExperienceSearchActive,
    hideExperienceFilter,
    setExperienceSearchResultsFromCarousel,
  })

  // Every explore category opens the results drawer, including when the current
  // map area returns nothing — the drawer then shows its empty state.
  const exploreDrawerResults = useMemo<ChatMapDrawerResults>(() => {
    return selectedExploreCategory?.source === 'google-places'
      ? { kind: 'places', places: explorePlaces }
      : { kind: 'offers', offers: exploreOffers }
  }, [exploreOffers, explorePlaces, selectedExploreCategory])

  // ── AI camera state ──

  const {
    aiCamera,
    aiCameraKey,
    moveMapTo,
    applyUiAction,
    handleAiCameraClear,
    resetCameraState,
  } = useAICameraState({ setRightPanelView })

  // ── Callbacks ──

  const clearFitBoundsRequest = useCallback(() => {
    setFitBoundsRequest(EMPTY_FIT_BOUNDS_REQUEST)
  }, [])

  const clearOfferPlaceSuggestionMarkers = useCallback(() => {
    setOfferSuggestionMarker(undefined)
    setPlaceSuggestionMarker(undefined)
  }, [])

  const clearChatMapMarkersRef = useRef<() => void>(noop)

  const clearOfferSuggestionMapState = useCallback(() => {
    clearOfferPlaceSuggestionMarkers()
    clearChatMapMarkersRef.current()
  }, [clearOfferPlaceSuggestionMarkers])

  const handleMapViewStateChangeWithCameraSnapshot = useCallback((viewState: Parameters<typeof handleMapViewStateChange>[0]) => {
    handleMapViewStateChange(viewState)
    if (!shouldSnapshotNextMapCameraRef.current) return
    shouldSnapshotNextMapCameraRef.current = false
    setLastMapCamera({
      lat: viewState.latitude,
      lng: viewState.longitude,
      zoom: viewState.zoom,
    })
  }, [handleMapViewStateChange])

  const handleMapUserMoveStart = useCallback(() => {
    shouldSnapshotNextMapCameraRef.current = true
    notifyUserMovedMap()
  }, [notifyUserMovedMap])

  const requestFitToCoordinates = useCallback((coordinates: Array<MapCoordinate>) => {
    const validCoordinates = coordinates.filter(isValidCoordinate)
    if (validCoordinates.length === 0) {
      clearFitBoundsRequest()
      return
    }
    setFitBoundsRequest(prev => ({
      coordinates: validCoordinates,
      key: prev.key + 1,
    }))
  }, [clearFitBoundsRequest])

  const mapAreaRef = useRef<HTMLDivElement>(null)

  const frameExploreCoordinates = useExploreCamera({
    category: selectedExploreCategory,
    target: exploreSearchTarget,
    isHandoffActive: !!exploreHandoff,
    paneRef: mapAreaRef,
    fitToCoordinates: requestFitToCoordinates,
  })

  useExploreHandoffCamera({
    exploreHandoff,
    exploreMarkers,
    fitToCoordinates: requestFitToCoordinates,
  })

  const requestFitToOfferCoordinates = useCallback((offers: Array<{ coordinates?: MapCoordinate }>) => {
    const coordinates = offers.flatMap((offer) => isValidCoordinate(offer.coordinates) ? [offer.coordinates] : [])
    if (coordinates.length === 0) {
      clearFitBoundsRequest()
      return
    }
    requestFitToCoordinates(coordinates)
  }, [clearFitBoundsRequest, requestFitToCoordinates])

  const {
    cruiseTransportMarkers,
    clearCruiseTransportMapState,
    handleCruiseTransportMapClick,
  } = useCruiseTransportMapMarkers({
    clearTransientMapState: clearOfferSuggestionMapState,
    hideHotelFilter,
    hideExperienceFilter,
    handleExploreCategorySelect,
    setRightPanelView,
    moveMapTo,
    requestFitToCoordinates,
    clearFitBoundsRequest,
  })

  const {
    chatMapMarkers,
    clearChatMapMarkers,
    handleChatMapOnMapClick,
  } = useChatMapOnMapMarkers({
    hideHotelFilter,
    hideExperienceFilter,
    handleExploreCategorySelect,
    clearCruiseTransportMapState,
    clearOfferPlaceSuggestionMarkers,
    setRightPanelView,
    moveMapTo,
    requestFitToCoordinates,
    clearFitBoundsRequest,
  })

  clearChatMapMarkersRef.current = clearChatMapMarkers

  const clearTransientMapState = useCallback(() => {
    clearOfferSuggestionMapState()
    clearCruiseTransportMapState()
    setFocusedPlaceId(undefined)
    setFocusedOfferId(undefined)
  }, [clearCruiseTransportMapState, clearOfferSuggestionMapState])

  // The drawer-triggered "Explore on map" origin converges on the same selectExploreCategory
  // state change as the chip-click path (handleExploreCategorySelectWithActivity above), so it
  // needs the same activity entry -- otherwise a follow-up "Search this area" click reports a
  // category the model was never told got selected.
  //
  // Gated on selectedExploreCategory?.key, same as the chip path's own dedup (there, re-clicking
  // the active chip is turned into `undefined` by the caller and handleExploreCategorySelectWithActivity
  // bails on it) -- this path has no such toggle convention (source is always a real category, never
  // undefined), so without this check, opening a second hotel drawer while Hotels is already
  // selected would dispatch a second identical entry even though selectExploreCategory itself no-ops.
  // Dispatched after handleExploreMapFromDrawer, not before -- reporting a selection before the call
  // that actually applies it risks the entry outliving its own cause if that call were ever gated.
  const handleExploreMapClick = useCallback((
    coordinates: { lat: number; lng: number },
    categoryKey: ChatExploreCategoryKey,
    destinationQueryText?: string,
  ) => {
    const category = chatExploreCategories.find(item => item.key === categoryKey)
    if (!category) return

    clearTransientMapState()
    frameExploreCoordinates(coordinates)
    const isAlreadySelected = selectedExploreCategory?.key === category.key
    handleExploreMapFromDrawer(
      category,
      destinationQueryText ? { coordinates, queryText: destinationQueryText } : undefined,
    )
    if (!isAlreadySelected) {
      eventBus.dispatch({
        type: 'trip_planner_surface_activity',
        entries: [buildMapExploreCategorySelectedActivityEntry(englishExploreCategoryLabel(category.key))],
      })
    }
  }, [clearTransientMapState, frameExploreCoordinates, handleExploreMapFromDrawer, eventBus, selectedExploreCategory])

  const handleHotelMapViewClick = useCallback((data: Parameters<typeof applyHotelFilterState>[0]) => {
    clearTransientMapState()
    applyHotelFilterState(data)
    requestFitToOfferCoordinates(data.offers)
  }, [applyHotelFilterState, clearTransientMapState, requestFitToOfferCoordinates])

  const handleShowExperienceOnMap = useCallback((data: Parameters<typeof applyExperienceFilterState>[0]) => {
    clearTransientMapState()
    applyExperienceFilterState(data)
    requestFitToOfferCoordinates(data.offers)
  }, [applyExperienceFilterState, clearTransientMapState, requestFitToOfferCoordinates])

  const handleOfferSuggestionMapClick = useCallback((data: OfferMapSuggestionData, camera?: MapCameraPosition) => {
    setFocusedOfferId(data.offerId)
    setFocusedPlaceId(undefined)
    hideHotelFilter()
    hideExperienceFilter()
    handleExploreCategorySelect(undefined)
    clearCruiseTransportMapState()
    clearChatMapMarkers()
    setRightPanelView('map')
    const suggestionMarker = createOfferSuggestionMarker(data)
    const isInItinerary = Boolean(getOfferItineraryDayRange(data.offerId, itineraryData))
    setOfferSuggestionMarker(
      isInItinerary
        ? {
            ...suggestionMarker,
            markerType: data.offerType === 'experience' ? MARKER_TYPE_CHAT_EXPERIENCE : MARKER_TYPE_CHAT_HOTEL,
            source: MARKER_SOURCE_ITINERARY,
          }
        : suggestionMarker,
    )
    moveMapTo(camera?.lat ?? data.coordinates.lat, camera?.lng ?? data.coordinates.lng, camera?.zoom ?? SHOW_ON_MAP_ZOOM)
  }, [hideHotelFilter, hideExperienceFilter, handleExploreCategorySelect, clearCruiseTransportMapState, clearChatMapMarkers, setRightPanelView, moveMapTo, itineraryData])

  const handlePlaceSuggestionMapClick = useCallback((data: PlaceMapSuggestionData, camera?: MapCameraPosition) => {
    setFocusedPlaceId(data.googlePlaceId)
    setFocusedOfferId(undefined)
    hideHotelFilter()
    hideExperienceFilter()
    handleExploreCategorySelect(undefined)
    clearCruiseTransportMapState()
    setOfferSuggestionMarker(undefined)
    clearChatMapMarkers()
    setRightPanelView('map')
    setPlaceSuggestionMarker(createPlaceSuggestionMarker(data))
    moveMapTo(camera?.lat ?? data.coordinates.lat, camera?.lng ?? data.coordinates.lng, camera?.zoom ?? PLACE_SHOW_ON_MAP_ZOOM)
  }, [hideHotelFilter, hideExperienceFilter, handleExploreCategorySelect, clearCruiseTransportMapState, clearChatMapMarkers, setRightPanelView, moveMapTo])

  const handleMarkerSelect = useCallback((data: MarkerSelectionData | undefined) => {
    if (!data) {
      onMarkerSelect(undefined)
      return
    }

    const { marker } = data
    if (marker.markerType === MARKER_TYPE_STOP) {
      onMarkerSelect(data)
      return
    }
    if (activatePlace(marker)) onMarkerSelect(undefined)
    else onMarkerSelect(data)
  }, [activatePlace, onMarkerSelect])

  const handleDeselectExploreCategory = useCallback(() => {
    handleExploreCategorySelect(undefined)
  }, [handleExploreCategorySelect])

  const handleExploreOfferClick = useCallback((data: { offerId: string; offerType?: string }) => {
    activateOffer(data)
  }, [activateOffer])

  const handleExplorePlaceClick = useCallback((place: ExploreItem) => {
    const marker = exploreMarkers.find(exploreMarker => exploreMarker.placeId === place.id)
    if (!marker) return
    handleMarkerSelect({ marker })
  }, [exploreMarkers, handleMarkerSelect])

  // Chip-click origin -- deselect (re-clicking the active chip calls this with `undefined`, per
  // ChatMapFilters' own toggle wrapper) must not fire the entry. The separate "Explore on map"
  // drawer-triggered origin (handleExploreMapFromDrawer, wrapped by handleExploreMapClick below)
  // converges on the same underlying selectExploreCategory state change one layer down, so it's
  // instrumented separately there, not routed through this callback.
  const handleExploreCategorySelectWithActivity = useCallback((category: ChatExploreCategory | undefined) => {
    handleExploreCategorySelect(category)
    if (!category) return
    eventBus.dispatch({
      type: 'trip_planner_surface_activity',
      entries: [buildMapExploreCategorySelectedActivityEntry(englishExploreCategoryLabel(category.key))],
    })
  }, [handleExploreCategorySelect, eventBus])

  // From too far out to search, this moves the camera instead. No entry for that case: the bounds
  // to report are the ones being left behind.
  const handleSearchThisAreaClickWithActivity = useCallback(() => {
    handleSearchThisAreaClick()
    if (exploreSearchTarget) {
      frameExploreCoordinates(exploreSearchTarget)
      return
    }
    if (!selectedExploreCategory || !mapBounds) return
    eventBus.dispatch({
      type: 'trip_planner_surface_activity',
      entries: [buildMapSearchThisAreaActivityEntry(englishExploreCategoryLabel(selectedExploreCategory.key), mapBounds)],
    })
  }, [handleSearchThisAreaClick, exploreSearchTarget, frameExploreCoordinates, eventBus, mapBounds, selectedExploreCategory])

  const handleExploreAskAi = useCallback(() => {
    if (!selectedExploreCategory) return
    const categoryLabel = intl.formatMessage(selectedExploreCategory.labelMessage)
    eventBus.dispatch({
      type: 'ask_ai_for_list_row',
      title: categoryLabel,
      context: `The user is looking at ${categoryLabel} on the map in the currently visible area`,
    })
  }, [eventBus, intl, selectedExploreCategory])

  // ── Clear transient overlays when leaving the map tab ──

  const prevRightPanelViewRef = useRef(rightPanelView)
  useEffect(() => {
    const prev = prevRightPanelViewRef.current
    prevRightPanelViewRef.current = rightPanelView
    if (prev === 'map' && rightPanelView !== 'map') {
      handleDeselectExploreCategory()
      // Enlarge / suggestion / cruise overlays should not linger over itinerary
      // markers after the user leaves the map (tabs, chat, timeline, etc.).
      clearTransientMapState()
    }
  }, [rightPanelView, handleDeselectExploreCategory, clearTransientMapState])

  // ── Event bus subscriptions ──

  useEventSubscription('hotel_search_results', (event) => {
    const accepted = setHotelSearchResults(event.data, event.generation)
    if (!accepted) return

    clearTransientMapState()
    hideExperienceFilter()
    if (event.data.offers.length === 0) {
      Analytics.trackClientEvent({
        subject: `${event.data.searchQuery}|chatId:${chatId}`,
        action: 'zero_search_results',
        category: 'chat_trip_planner',
        type: 'operational',
      })
    }
  })

  useEventSubscription('hotel_search_error', (event) => {
    const accepted = setHotelSearchError(event.data.message, event.generation, event.data.hotelSearchCallId)
    if (!accepted) return

    clearCruiseTransportMapState()
  })

  useEventSubscription('experience_search_results', (event) => {
    const accepted = setExperienceSearchResults(event.data, event.generation)
    if (!accepted) return

    clearTransientMapState()
    hideHotelFilter()
  })

  useEventSubscription('experience_search_error', (event) => {
    const accepted = setExperienceSearchError(event.data.message, event.generation, event.data.searchCallId)
    if (!accepted) return

    clearCruiseTransportMapState()
  })

  useEventSubscription('ui_action', (event) => {
    // AI camera / panel moves are unrelated map actions — drop sticky enlarge pins.
    clearTransientMapState()
    applyUiAction(event.data)
    clearPendingUiAction()
  })

  // Apply buffered UI action that arrived before this component mounted
  // (e.g., during the landing→chat transition on first message)
  useEffect(() => {
    if (pendingUiAction) {
      clearTransientMapState()
      applyUiAction(pendingUiAction)
      clearPendingUiAction()
    }
  }, [pendingUiAction, applyUiAction, clearTransientMapState, clearPendingUiAction])

  useEventSubscription('show_hotel_on_map', (event) => {
    handleHotelMapViewClick(event.data)
  })

  useEventSubscription('show_experience_on_map', (event) => {
    handleShowExperienceOnMap(event.data)
  })

  useEventSubscription('show_offer_suggestion_on_map', (event) => {
    handleOfferSuggestionMapClick(event.data, event.camera)
  })

  useEventSubscription('show_place_on_map', (event) => {
    handlePlaceSuggestionMapClick(event.data, event.camera)
  })

  useEventSubscription('show_chat_map_on_map', (event) => {
    handleChatMapOnMapClick(event.data)
  })

  useEventSubscription('show_cruise_transport_on_map', (event) => {
    handleCruiseTransportMapClick(event.points)
  })

  useEventSubscription('show_explore_on_map', (event) => {
    handleExploreMapClick(event.coordinates, event.categoryKey, event.destinationQueryText)
  })

  useEventSubscription('itinerary_changed', () => {
    hideHotelFilter()
    hideExperienceFilter()
    resetFilterState()
    clearTransientMapState()
  })

  useEventSubscription('reset_chat_state', () => {
    resetHotelSearch()
    resetExperienceSearch()
    resetFilterState()
    resetCameraState()
    shouldSnapshotNextMapCameraRef.current = false
    setLastMapCamera(undefined)
    // mapStyleMode is owned by ChatPageContent / TripChatMobileSurfaceContent and
    // reset there — MapContainer is lazy-mounted on mobile and may miss this event.
    clearTransientMapState()
  })

  // ── Map data + marker merging ──

  const { mapData: baseMapData, hasMapMarkers: hasBaseMapMarkers } = useMapData({
    messages,
    selectedDayItineraryId,
    selectedStop,
    itineraryResult,
  })

  // Streaming counts: location entities resolved mid-stream become map pins. Explore uses
  // the initial-load flag, not the fetching one, so a passive refetch cannot flash the pill.
  const isMapUpdating =
    isExploreLoading ||
    enrichingStopIds.size > 0 || enrichingItemIds.size > 0 ||
    loadingState.isContentStreaming || isAwaitingMessageEnd

  const itineraryExperienceMarkers = useMemo(() => {
    return (baseMapData.markers ?? []).filter(marker =>
      marker.source === MARKER_SOURCE_ITINERARY && marker.markerType === MARKER_TYPE_CHAT_EXPERIENCE,
    )
  }, [baseMapData.markers])

  const itineraryExperienceOfferIds = useMemo(() => {
    const offerIds = new Set<string>()

    itineraryData?.stops.forEach((stop) => {
      stop.days.forEach((day) => {
        day.items.forEach((item) => {
          if (isExperienceSavedData(item.savedItemData)) {
            offerIds.add(item.savedItemData.experienceId)
          }
        })
      })

      stop.itemsWithoutDay?.forEach((item) => {
        if (isExperienceSavedData(item.savedItemData)) {
          offerIds.add(item.savedItemData.experienceId)
        }
      })
    })

    return offerIds
  }, [itineraryData])

  const ownedExploreExperienceMarkers = useMemo(() => {
    return exploreMarkers.map((marker) => {
      if (marker.markerType !== 'explore-experience') {
        return marker
      }

      const matchingItineraryMarker = itineraryExperienceMarkers.find((itineraryMarker) => {
        return marker.offerId && itineraryMarker.offerId === marker.offerId
      })

      if (!matchingItineraryMarker && (!marker.offerId || !itineraryExperienceOfferIds.has(marker.offerId))) {
        return marker
      }

      return {
        ...marker,
        markerId: matchingItineraryMarker?.markerId ?? marker.markerId,
        offerId: matchingItineraryMarker?.offerId ?? marker.offerId,
        markerType: MARKER_TYPE_CHAT_EXPERIENCE,
        source: MARKER_SOURCE_ITINERARY,
      }
    })
  }, [exploreMarkers, itineraryExperienceMarkers, itineraryExperienceOfferIds])

  const mergedMarkers = useMemo(() => {
    const hasFilterResults =
      (isHotelSearchActive && hotelMapMarkers.length > 0) ||
      (isExperienceSearchActive && experienceMapMarkers.length > 0) ||
      (isExploreFilterActive && exploreMarkers.length > 0)
    // Only hide suggested markers when filter results are ready to replace them.
    // During the loading gap, suggested markers stay visible to preserve the
    // viewport and prevent useMapGlobeRotation from jumping to the default view.
    const base = hasFilterResults
      ? (baseMapData.markers ?? []).filter(m => m.source !== 'suggested')
      : (baseMapData.markers ?? [])
    // Priority: hotel > experience > explore > base
    if (isHotelSearchActive && hotelMapMarkers.length > 0) return [...hotelMapMarkers, ...base]
    if (isExperienceSearchActive && experienceMapMarkers.length > 0) return [...experienceMapMarkers, ...base]
    if (isExploreFilterActive && exploreMarkers.length > 0) {
      const ownedMarkerKeys = new Set(
        ownedExploreExperienceMarkers
          .filter(marker => marker.source === MARKER_SOURCE_ITINERARY)
          .map(getMarkerIdentityKey),
      )
      const dedupedBase = base.filter(marker => !ownedMarkerKeys.has(getMarkerIdentityKey(marker)))
      const result = [...ownedExploreExperienceMarkers, ...dedupedBase]
      return result
    }
    if (offerSuggestionMarker) {
      const hasOwnedMarker = base.some(marker => marker.offerId === offerSuggestionMarker.offerId)
      return hasOwnedMarker ? base : [offerSuggestionMarker, ...base]
    }
    if (placeSuggestionMarker) {
      const hasOwnedMarker = base.some(marker => marker.placeId === placeSuggestionMarker.placeId)
      return hasOwnedMarker ? base : [placeSuggestionMarker, ...base]
    }
    if (chatMapMarkers.length > 0) {
      const ownedPlaceIds = new Set(
        chatMapMarkers
          .map(marker => marker.placeId)
          .filter((placeId): placeId is string => Boolean(placeId)),
      )
      const dedupedBase = ownedPlaceIds.size === 0
        ? base
        : base.filter(marker => !marker.placeId || !ownedPlaceIds.has(marker.placeId))
      return [...chatMapMarkers, ...dedupedBase]
    }
    if (cruiseTransportMarkers.length > 0) return [...cruiseTransportMarkers, ...base]
    return base
  }, [
    baseMapData.markers,
    hotelMapMarkers,
    experienceMapMarkers,
    exploreMarkers,
    ownedExploreExperienceMarkers,
    isHotelSearchActive,
    isExperienceSearchActive,
    isExploreFilterActive,
    offerSuggestionMarker,
    placeSuggestionMarker,
    chatMapMarkers,
    cruiseTransportMarkers,
  ])

  const mapData = useMemo(() => ({
    ...baseMapData,
    markers: mergedMarkers,
  }), [baseMapData, mergedMarkers])

  const focusedItineraryItemId = useMemo(() => {
    if (!focusedOfferId && !focusedPlaceId) return undefined
    return itineraryResult.markers.find(marker => marker.itemId !== undefined &&
      (focusedOfferId ? marker.offerId === focusedOfferId : marker.placeId === focusedPlaceId))?.itemId
  }, [itineraryResult.markers, focusedOfferId, focusedPlaceId])

  const hasMapMarkers = hasBaseMapMarkers ||
    (isHotelSearchActive && hotelMapMarkers.length > 0) ||
    (isExperienceSearchActive && experienceMapMarkers.length > 0) ||
    (isExploreFilterActive && exploreMarkers.length > 0) ||
    Boolean(offerSuggestionMarker) ||
    Boolean(placeSuggestionMarker) ||
    chatMapMarkers.length > 0 ||
    cruiseTransportMarkers.length > 0

  const shouldResetToGlobe = !hasMapMarkers && !aiCamera && !lastMapCamera

  // ── i18n ──

  const searchThisAreaLabel = intl.formatMessage({
    id: 'kVSN4s5ZBcxyXhOZlUJO',
    defaultMessage: 'Search this area',
    description: 'Button label to search for offers in the currently visible map area',
  })

  // Convention exception: "Components should not determine whether they render"
  // This container must stay mounted so hooks (useSearchResults, useMapFilterState,
  // useAICameraState) preserve state across Map↔Timeline/List view toggles. The parent
  // controls visibility via the `visible` prop; the child only gates first mount
  // and then CSS-hides the live map.
  if (visible && !hasMountedMap) {
    setHasMountedMap(true)
  }
  if (!hasMountedMap) return null

  return (
    <FullHeightMapWrapper
      aria-hidden={!visible}
      className={visible ? undefined : MAP_KEEP_ALIVE_HIDDEN_CLASS}
      data-testid="trip-chat-map-container"
    >
      <MapArea ref={mapAreaRef}>
        {visible && (
          <MapOverlayStack>
            <ChatMapFilters
              hotelSearchQuery={hotelSearchQuery}
              isHotelFilterVisible={isHotelFilterVisible}
              onHotelFilterDismiss={handleHotelFilterDismiss}
              experienceSearchQuery={experienceSearchQuery}
              isExperienceFilterVisible={isExperienceFilterVisible}
              onExperienceFilterDismiss={handleExperienceFilterDismiss}
              isExploreFiltersVisible={isExploreFilterVisible}
              selectedExploreCategory={selectedExploreCategory}
              onExploreCategorySelect={handleExploreCategorySelectWithActivity}
              isExploreFetching={isExploreFetching}
            />
            {shouldShowSearchThisArea && (
              <SearchThisAreaButton
                kind={overlayButtonKind}
                variant="dark"
                size="small"
                shape="round"
                startIcon={<LineSearchIcon />}
                onClick={handleSearchThisAreaClickWithActivity}
                disabled={isSearchThisAreaDisabled}
              >
                {searchThisAreaLabel}
              </SearchThisAreaButton>
            )}
            {isMapUpdating && (
              <MapUpdatingIndicatorPill>
                <LoadingAnimation size={8} />
              </MapUpdatingIndicatorPill>
            )}
          </MapOverlayStack>
        )}
        <ChatMapWidget
          key="trip-planner-map"
          chatKey={chatKey}
          data={mapData}
          fillContainer
          onMarkerSelect={handleMarkerSelect}
          highlightedMarkerId={highlightedMarkerId}
          focusedMarkerId={focusedOfferId ?? focusedPlaceId}
          focusedItemId={focusedItineraryItemId}
          onViewStateChange={handleMapViewStateChangeWithCameraSnapshot}
          onUserMoveStart={handleMapUserMoveStart}
          onBoundsChange={handleMapBoundsChange}
          isDrawerOpen={Boolean(selectedMarkerData)}
          shouldResetToGlobe={shouldResetToGlobe}
          deemphasizeBaseMarkers={deemphasizeBaseMarkers}
          exploreOffers={exploreOffers}
          disableClustering={disableClustering}
          onMarkerHover={onMarkerHover}
          onOfferClick={activateOffer}
          itineraryData={itineraryData}
          onSendMessage={sendMessage}
          initialCamera={lastMapCamera}
          aiCamera={aiCamera}
          aiCameraKey={aiCameraKey}
          fitBoundsCoordinates={fitBoundsRequest.coordinates}
          fitBoundsKey={fitBoundsRequest.key}
          onFitBoundsHandled={clearFitBoundsRequest}
          onAiCameraClear={handleAiCameraClear}
          mapStyleMode={mapStyleMode}
          onMapStyleModeChange={onMapStyleModeChange}
          isViewportActive={visible}
        />
      </MapArea>
      {visible && selectedExploreCategory && (
        <ChatMapOffersDrawer
          isOpen={isExploreFilterActive}
          onClose={handleDeselectExploreCategory}
          category={selectedExploreCategory}
          results={exploreDrawerResults}
          isLoading={isExploreLoading}
          isError={isExploreError}
          isPartial={isExplorePartial}
          onRetry={refetchExplore}
          itineraryData={itineraryData}
          onSendMessage={sendMessage}
          onViewOffer={handleExploreOfferClick}
          onViewPlace={handleExplorePlaceClick}
          onAskAi={handleExploreAskAi}
          highlightedMarkerId={highlightedMarkerId}
          onCardHover={onCardHover}
        />
      )}
    </FullHeightMapWrapper>
  )
}

export default MapContainer
